import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    type DraggableAttributes,
    type DraggableSyntheticListeners,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitConfig,
    type IncomeSplitRule,
    type IncomeSplitRuleSet,
    IncomeSplitRuleSetCard,
} from '@fruitful/income-split';
import type { ImAccount } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { sortBy } from 'lodash';
import React, { useEffect } from 'react';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { ArrowStraight, View, YStack } from 'smoothie';

import { uuid } from '../../../common/misc';
import {
    incomeSplitAtom,
    incomeSplitRuleEditorModeAtom,
    incomeSplitRuleSetEditorModeAtom,
    selectedIncomeRuleAtom,
    selectedRuleSetAtom,
} from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';

const SortableGroup = ({
    group,
    children,
}: {
    group: IncomeSplitRuleSet;
    children: (props: {
        setActivatorNodeRef: (el: HTMLElement | null) => void;
        listeners?: DraggableSyntheticListeners;
        attributes?: DraggableAttributes;
    }) => React.ReactNode;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
    } = useSortable({ id: group.id ?? uuid() });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        marginBottom: 16,
    };

    return (
        <div ref={setNodeRef} style={style}>
            {children({
                setActivatorNodeRef,
                listeners,
                attributes,
            })}
        </div>
    );
};

const Group = ({
    group,
    isSorting,
    externalAccounts,
    bankAccountDetails,
    internalInvestmentAccounts,
    setSelectedRule,
    setSelectedRuleSet,
    setIncomeSplitRuleEditorMode,
    setIncomeSplitRuleSetEditorMode,
    setEditRuleSheetOpen,
    setActivatorNodeRef,
    listeners,
    attributes,
    showSingleRuleCard,
}: {
    group: IncomeSplitRuleSet;
    isSorting: boolean;
    externalAccounts: DepositAccount[];
    bankAccountDetails: BankAccountDisplayDetail[];
    internalInvestmentAccounts?: ImAccount[];
    setSelectedRule: (rule: Partial<IncomeSplitRule>) => void;
    setSelectedRuleSet: (ruleSet: IncomeSplitRuleSet) => void;
    setIncomeSplitRuleEditorMode: (mode: 'Edit' | 'Add') => void;
    setIncomeSplitRuleSetEditorMode: (mode: 'Edit' | 'Add') => void;
    setEditRuleSheetOpen: (isOpen: boolean) => void;
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    showSingleRuleCard: boolean;
}) => {
    const opacity = useSharedValue(1);

    const arrowAnimatedStyle = useAnimatedStyle(() => ({
        alignSelf: 'center',
        paddingVertical: 16,
        opacity: withTiming(opacity.value, {
            duration: 100,
            easing: Easing.bezier(0.65, 0, 0.35, 1),
        }),
    }));

    useEffect(() => {
        opacity.value = isSorting ? 0 : 1;
    }, [isSorting]);

    return (
        <YStack>
            <Animated.View>
                <View>
                    <IncomeSplitRuleSetCard
                        group={group}
                        context="edit"
                        bankAccountDetails={bankAccountDetails}
                        externalAccounts={externalAccounts}
                        internalInvestmentAccounts={
                            internalInvestmentAccounts ?? []
                        }
                        setSelectedRule={setSelectedRule}
                        onPressEdit={() => {
                            setIncomeSplitRuleSetEditorMode('Edit');
                            setSelectedRuleSet(group);
                            setEditRuleSheetOpen(true);
                        }}
                        onEditRule={() => {
                            setIncomeSplitRuleEditorMode('Edit');
                            router.navigate(
                                '/income-split/edit?tiedRules=true',
                            );
                        }}
                        onAddRule={(rule) => {
                            setSelectedRule(rule);
                            setIncomeSplitRuleEditorMode('Add');
                            router.navigate(
                                '/income-split/edit?tiedRules=true',
                            );
                        }}
                        setActivatorNodeRef={setActivatorNodeRef}
                        listeners={listeners}
                        attributes={attributes}
                        showSingleRuleCard={showSingleRuleCard}
                    />
                </View>
            </Animated.View>
            <Animated.View style={[{ marginLeft: 12 }, arrowAnimatedStyle]}>
                <ArrowStraight />
            </Animated.View>
        </YStack>
    );
};

const IncomeSplitRules = ({
    incomeSplit,
    externalAccounts,
    internalInvestmentAccounts,
    isSorting,
    setIsSorting,
    setEditRuleSheetOpen,
}: {
    incomeSplit: IncomeSplitConfig;
    externalAccounts: DepositAccount[];
    internalInvestmentAccounts?: ImAccount[];
    isSorting: boolean;
    setIsSorting: (val: boolean) => void;
    setEditRuleSheetOpen: (isOpen: boolean) => void;
}) => {
    const setIncomeSplit = useSetAtom(incomeSplitAtom);
    const setSelectedRule = useSetAtom(selectedIncomeRuleAtom);
    const setSelectedRuleSet = useSetAtom(selectedRuleSetAtom);
    const setIncomeSplitRuleEditorMode = useSetAtom(
        incomeSplitRuleEditorModeAtom,
    );
    const setIncomeSplitRuleSetEditorMode = useSetAtom(
        incomeSplitRuleSetEditorModeAtom,
    );
    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);

    const sortedGroups = sortBy(incomeSplit.ruleSets ?? [], 'priority');

    const firstQualifyingId = sortedGroups.find((group) => {
        const rules = group.rules ?? [];
        const firstRule = rules[0];
        return (
            rules.length === 1 &&
            firstRule?.percentage === 100 &&
            (firstRule?.targetBalanceCents == null ||
                firstRule.targetBalanceCents === 0) &&
            (firstRule?.monthlyContributionMaxCents == null ||
                firstRule.monthlyContributionMaxCents === 0) &&
            (firstRule?.yearlyContributionMaxCents == null ||
                firstRule.yearlyContributionMaxCents === 0) &&
            group.priority !== sortedGroups.length
        );
    })?.id;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { delay: 100, tolerance: 5 },
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setIsSorting(false);
        if (!over || active.id === over.id) return;

        const oldIndex = sortedGroups.findIndex((g) => g.id === active.id);
        const newIndex = sortedGroups.findIndex((g) => g.id === over.id);

        const reordered = arrayMove(sortedGroups, oldIndex, newIndex).map(
            (group, i) => ({
                ...group,
                priority: i + 1,
            }),
        );

        setIncomeSplit({ ...incomeSplit, ruleSets: reordered });
    };

    return (
        <YStack>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={() => {
                    setIsSorting(true);
                }}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToParentElement]}
            >
                <SortableContext
                    items={sortedGroups.map((g) => g?.id ?? '')}
                    strategy={verticalListSortingStrategy}
                >
                    {sortedGroups.map((group) => (
                        <SortableGroup key={group.id} group={group}>
                            {({
                                setActivatorNodeRef,
                                listeners,
                                attributes,
                            }) => (
                                <Group
                                    group={group}
                                    isSorting={isSorting}
                                    externalAccounts={externalAccounts}
                                    bankAccountDetails={bankAccountDetails}
                                    internalInvestmentAccounts={
                                        internalInvestmentAccounts
                                    }
                                    setSelectedRule={setSelectedRule}
                                    setSelectedRuleSet={setSelectedRuleSet}
                                    setIncomeSplitRuleEditorMode={
                                        setIncomeSplitRuleEditorMode
                                    }
                                    setIncomeSplitRuleSetEditorMode={
                                        setIncomeSplitRuleSetEditorMode
                                    }
                                    setEditRuleSheetOpen={setEditRuleSheetOpen}
                                    setActivatorNodeRef={setActivatorNodeRef}
                                    listeners={listeners}
                                    attributes={attributes}
                                    showSingleRuleCard={
                                        group.id === firstQualifyingId
                                    }
                                />
                            )}
                        </SortableGroup>
                    ))}
                </SortableContext>
            </DndContext>
        </YStack>
    );
};

export default IncomeSplitRules;
